/**
 * BurndownSparkline — a failure-analysis eval card's chart: the task
 * score distribution (easiest → hardest) in the Sparkline frame (2:1
 * aspect, faint grid, tiny % axis). Plots whichever series (highest /
 * average / lowest) the benchmark page has selected — each sorted into
 * its own curve using the continuous score gradient; invisible dots
 * make every task hoverable with its name, rank, and score.
 */

import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  type TooltipContentProps,
} from 'recharts'

import { scoreTone } from '@/components/evals/analysis'
import { SERIES_LABELS, type ScoreSeries } from './benchmarkAnalysis'
import { cn } from '@/lib/utils'
import type { BenchmarkCurvePoint } from '@/api/generated/model'

interface BurndownPoint {
  x: number
  score: number
  name: string
  slug: string
  seriesLabel: string
}

/** Max points per series — a card is ~450px wide, so anything beyond this
 * is invisible but still costs DOM nodes and hover hit-testing (the OTS
 * corpus is 2,674 tasks). Rank labels stay true to the full corpus. */
const MAX_POINTS = 240

function toPoints(curve: BenchmarkCurvePoint[], series: ScoreSeries): BurndownPoint[] {
  const sorted = curve
    .map((point) => ({ point, value: point[series] }))
    .sort((a, b) => b.value - a.value || a.point.slug.localeCompare(b.point.slug))
  const step = Math.max(1, Math.ceil(sorted.length / MAX_POINTS))
  return sorted
    .map(({ point, value }, index) => ({
      x: index + 1,
      score: value * 100,
      name: point.name,
      slug: point.slug,
      seriesLabel: SERIES_LABELS[series],
    }))
    .filter((_, index) => index % step === 0 || index === sorted.length - 1)
}

/** Gradient stops sampled along the curve (same approach as the big chart). */
function gradientStops(points: BurndownPoint[]): { offset: string; color: string }[] {
  const sampleCount = Math.min(25, points.length)
  const stops: { offset: string; color: string }[] = []
  for (let sample = 0; sample < sampleCount; sample++) {
    const index = Math.round((sample / (sampleCount - 1)) * (points.length - 1))
    const point = points[index]
    if (point === undefined) {
      throw new Error(`unreachable: burndown gradient sample ${sample.toString()} out of range`)
    }
    stops.push({
      offset: `${((sample / (sampleCount - 1)) * 100).toFixed(1)}%`,
      color: scoreTone(point.score / 100),
    })
  }
  return stops
}

function BurndownTooltip({ tooltip }: { tooltip: TooltipContentProps }): React.JSX.Element | null {
  const { active, payload } = tooltip
  if (!active) return null
  const entry = payload.find((item) => item.dataKey === 'score')
  if (entry === undefined) return null
  const datum = entry.payload as BurndownPoint
  return (
    <div className="max-w-72 rounded-lg border border-border bg-card p-2 text-xs shadow-sm">
      <div className="flex items-baseline justify-between gap-3">
        <p className="truncate font-medium text-foreground">{datum.name}</p>
        <p className="shrink-0 font-mono text-muted-foreground">#{datum.x}</p>
      </div>
      <p className="mt-0.5 text-muted-foreground">
        <span className="font-medium tabular-nums text-foreground">{datum.score.toFixed(1)}%</span>{' '}
        {datum.seriesLabel.toLowerCase()} · {datum.slug}
      </p>
    </div>
  )
}

export function BurndownSparkline({
  curve,
  series = ['best'],
  className,
}: {
  curve: BenchmarkCurvePoint[]
  /** Which statistics to plot; defaults to the highest-score curve. */
  series?: ScoreSeries[]
  className?: string
}): React.JSX.Element | null {
  if (curve.length < 2) return null
  const plotted = series.map((key) => ({ key, points: toPoints(curve, key) }))
  const first = plotted[0]
  if (first === undefined) return null
  // Ranks survive downsampling, so the axis spans the full corpus.
  const lastRank = first.points[first.points.length - 1]?.x ?? first.points.length
  const idBase = `burndown-${curve[0]?.slug ?? 'curve'}`

  return (
    <div
      className={cn(
        // recharts makes the svg focusable; suppress the focus ring on click.
        'w-full outline-none [&_.recharts-wrapper]:outline-none [&_svg]:outline-none',
        className
      )}
    >
      {/* 2:1 width:height — all eval charts share this aspect ratio. */}
      <ResponsiveContainer width="100%" aspect={2}>
        <ScatterChart accessibilityLayer={false} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
          <defs>
            {plotted.map(({ key, points }) => (
              <linearGradient key={key} id={`${idBase}-${key}`} x1="0" y1="0" x2="1" y2="0">
                {gradientStops(points).map((stop) => (
                  <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
                ))}
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid stroke="hsl(var(--border) / 0.6)" />
          <XAxis
            dataKey="x"
            name="Task rank"
            type="number"
            domain={[1, lastRank]}
            ticks={[1, lastRank]}
            tickFormatter={(value: number) => (value === 1 ? 'easiest' : 'hardest')}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            tickLine={false}
            axisLine={false}
            height={16}
          />
          <YAxis
            dataKey="score"
            name="Score"
            domain={[0, 100]}
            ticks={[0, 50, 100]}
            tickFormatter={(value: number) => `${value.toString()}%`}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            tickLine={false}
            axisLine={false}
            width={34}
          />
          <ZAxis range={[10, 10]} />
          <Tooltip
            shared={false}
            cursor={false}
            isAnimationActive={false}
            // Hoverable popover: once the pointer enters it the chart stops
            // receiving mousemove, so it stays put instead of fleeing.
            wrapperStyle={{ pointerEvents: 'auto' }}
            content={(tooltip) => <BurndownTooltip tooltip={tooltip} />}
          />
          {plotted.map(({ key, points }) => (
            <Scatter
              key={`line-${key}`}
              data={points}
              shape={() => <g />}
              tooltipType="none"
              legendType="none"
              line={{
                stroke: `url(#${idBase}-${key})`,
                strokeWidth: 3,
                strokeLinecap: 'round',
              }}
              lineJointType="monotone"
              isAnimationActive={false}
            />
          ))}
          {/* Invisible hover targets so every task names itself on hover. */}
          {plotted.map(({ key, points }) => (
            <Scatter
              key={`dots-${key}`}
              data={points}
              shape={(props: { cx?: number; cy?: number }) =>
                props.cx !== undefined && props.cy !== undefined ? (
                  <circle cx={props.cx} cy={props.cy} r={6} fill="transparent" />
                ) : (
                  <g />
                )
              }
              isAnimationActive={false}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  )
}
